import { useContext, type JSX } from 'react';
import {
    HalfSlideIn,
    SecondLevelHeader,
    SlideInBody,
    SlideInHeaderTexts,
} from '../../ui/slidein/Slidein.tsx';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import * as PermitEditState from '../state/PermitEditState.ts';
import * as PermitSlideInTexts from '../i18n/PermitSlideInTexts.ts';
import { Translation } from '../../common/i18n/Text.ts';
import * as Http from '../../api/Http.ts';
import { css } from '@emotion/css';
import { OperatorTypo } from '../../ui/OperatorTypo.ts';
import { ColorHex } from '../../ui/Colors.ts';
import { urlWithOperatorExternalId } from '../../utils/Utils.ts';
import { OperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';

export interface Texts {
    Caption: Translation;
    Heading: Translation;
    PermitPrint: Translation;
    VignettePrint: (lp: string) => string;
}

const PrintLink = (p: { link: string; description: string }) => {
    return (
        <a
            className={css({
                display: 'block',
                textDecoration: 'none',
                ...OperatorTypo.body,
                color: ColorHex.darkblue,
                padding: '4px 48px',
                margin: '0 -48px',
                '&:hover': {
                    backgroundColor: ColorHex.rgba(ColorHex.lightblue, 0.25),
                },
            })}
            target="_blank"
            href={p.link}
            rel="noreferrer"
        >
            {p.description}
        </a>
    );
};

export function PrintSelectionSlideIn() {
    const { operatorExternalId } = useContext(OperatorContext);
    const { storeState, update } = useStore(store => ({
        layout: PermitEditState.Layout.get(store),
        server: PermitEditState.Server.get(store),
    }));
    const { language } = useLanguage();

    function txt(): Texts {
        return PermitSlideInTexts.print[language];
    }

    function renderElements(): JSX.Element[] {
        const permit = storeState.server.data;
        if (permit) {
            const links = [
                <PrintLink
                    description={txt().PermitPrint()}
                    link={urlWithOperatorExternalId(
                        Http.OperatorAccount.Permits.pdfPrint(
                            permit.contractExternalId,
                        ),
                        operatorExternalId,
                    )}
                    key="permit-pdf"
                />,
            ];
            for (const v of permit.vignetteSideprint) {
                links.push(
                    <PrintLink
                        description={txt().VignettePrint(v.licensePlateNr)}
                        link={urlWithOperatorExternalId(
                            Http.OperatorAccount.LicensePlates.vignettePdf2(
                                v.id,
                            ),
                            operatorExternalId,
                        )}
                        key={`vignettePdf${v.id}`}
                    />,
                );
            }
            return links;
        } else {
            return [];
        }
    }

    return (
        <HalfSlideIn open={storeState.layout.showPrintSlideIn} outsideBody>
            <SecondLevelHeader
                onClose={() =>
                    update(store =>
                        PermitEditState.Layout.stateWrite(store, {
                            showPrintSlideIn: false,
                        }),
                    )
                }
            >
                <SlideInHeaderTexts
                    subtitle={txt().Caption()}
                    title={txt().Heading()}
                    hasLeftIcon={false}
                />
            </SecondLevelHeader>
            <SlideInBody>{renderElements()}</SlideInBody>
        </HalfSlideIn>
    );
}
